import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import CloseIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';
import TrashIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Trash.js';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import * as styles from './FilterPopover.styles';

export interface FilterPopoverRow<TColumn extends string = string> {
  id: string;
  column: TColumn | '';
}

export interface FilterPopoverProps<TColumn extends string, TValue> {
  readonly anchorEl: HTMLElement | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly rows: FilterPopoverRow<TColumn>[];
  readonly onRowsChange: (rows: FilterPopoverRow<TColumn>[]) => void;
  readonly value: TValue;
  readonly onChange: (value: TValue) => void;
  readonly onApply: (value: TValue, rows: FilterPopoverRow<TColumn>[]) => void;
  readonly onRemoveAll: () => void;
  readonly makeId: () => string;
  readonly columnOptions: { value: TColumn; label: string }[];
  readonly clearColumnValue: (column: TColumn, value: TValue) => TValue;
  readonly renderValueControl: (
    row: FilterPopoverRow<TColumn>,
  ) => React.ReactNode;
  readonly testIdPrefix: string;
  readonly columnLabel?: string;
}

export function FilterPopover<TColumn extends string, TValue>({
  anchorEl,
  open,
  onClose,
  rows,
  onRowsChange,
  value,
  onChange,
  onApply,
  onRemoveAll,
  makeId,
  columnOptions,
  clearColumnValue,
  renderValueControl,
  testIdPrefix,
  columnLabel = 'Column',
}: FilterPopoverProps<TColumn, TValue>): React.ReactElement {
  const chosenColumns = useMemo(
    () => rows.map((r) => r.column).filter(Boolean),
    [rows],
  );

  const handleColumnChange = (rowId: string, nextColumn: TColumn) => {
    const prevColumn = rows.find((r) => r.id === rowId)?.column;
    if (prevColumn && prevColumn !== nextColumn) {
      onChange(clearColumnValue(prevColumn as TColumn, value));
    }
    onRowsChange(
      rows.map((r) => (r.id === rowId ? { ...r, column: nextColumn } : r)),
    );
  };

  const handleAddFilter = () => {
    onRowsChange([...rows, { id: makeId(), column: '' }]);
  };

  const handleRemoveRow = (rowId: string) => {
    const removed = rows.find((r) => r.id === rowId)?.column;
    const nextRows = rows.filter((r) => r.id !== rowId);
    onRowsChange(nextRows);
    if (removed) onChange(clearColumnValue(removed as TColumn, value));
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: styles.popoverPaper } }}
    >
      <Box
        sx={styles.container}
        data-testid={`${testIdPrefix}-filter-popover`}
        onKeyDown={(e) => {
          if (
            e.key === 'Enter' &&
            !e.nativeEvent.isComposing &&
            e.target instanceof HTMLInputElement
          ) {
            e.preventDefault();
            onApply(value, rows);
            onClose();
          }
        }}
      >
        <Stack spacing={1.5}>
          {rows.map((row, index) => (
            <Stack
              key={row.id}
              direction="row"
              spacing={1.5}
              sx={styles.row}
              data-testid={`${testIdPrefix}-filter-row`}
            >
              <IconButton
                aria-label="Remove filter"
                size="small"
                disabled={rows.length <= 1}
                onClick={() => handleRemoveRow(row.id)}
                data-testid={`${testIdPrefix}-filter-remove-row`}
                sx={styles.removeIcon}
              >
                <CloseIcon size="md" />
              </IconButton>

              <Box sx={styles.logicControl}>
                {index > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ lineHeight: '40px', textAlign: 'center' }}
                  >
                    And
                  </Typography>
                )}
              </Box>

              <FormControl size="small" sx={styles.field}>
                <InputLabel shrink>{columnLabel}</InputLabel>
                <Select
                  displayEmpty
                  label={columnLabel}
                  value={row.column}
                  onChange={(e) =>
                    handleColumnChange(row.id, e.target.value as TColumn)
                  }
                  inputProps={{ 'aria-label': 'Choose a column' }}
                  renderValue={(selected) =>
                    selected ? (
                      columnOptions.find((c) => c.value === selected)?.label
                    ) : (
                      <Typography component="span" sx={styles.placeholderText}>
                        Choose a column
                      </Typography>
                    )
                  }
                >
                  {columnOptions.map((col) => (
                    <MenuItem
                      key={col.value}
                      value={col.value}
                      disabled={
                        col.value !== row.column &&
                        chosenColumns.includes(col.value)
                      }
                    >
                      {col.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {renderValueControl(row)}
            </Stack>
          ))}
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          sx={styles.footer}
        >
          <Button
            variant="text"
            sx={styles.footerButton}
            onClick={handleAddFilter}
            data-testid={`${testIdPrefix}-filter-add`}
            startIcon={<AddIcon />}
          >
            Add filter
          </Button>
          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              color="inherit"
              onClick={onRemoveAll}
              sx={styles.footerButton}
              data-testid={`${testIdPrefix}-filter-remove-all`}
              startIcon={<TrashIcon />}
            >
              Remove all
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                onApply(value, rows);
                onClose();
              }}
              data-testid={`${testIdPrefix}-filter-apply`}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Popover>
  );
}
